import { capitaliseFirstLetter } from '../../utils';
import { Currency } from '../../locale/currencies';
import { impactQuantities, ImpactValues } from './impact';
import { CawTier } from './tiers';

const CAW_TIER_PRICES: Record<CawTier, Record<Currency, number>> = {
  [CawTier.SEED]: {
    GBP: 6,
    USD: 9,
    EUR: 8,
    AUD: 13,
    CAD: 12,
  },
  [CawTier.GROW]: {
    GBP: 15,
    USD: 22,
    EUR: 19,
    AUD: 32,
    CAD: 30,
  },
  [CawTier.BLOOM]: {
    GBP: 28,
    USD: 41,
    EUR: 35,
    AUD: 59,
    CAD: 57,
  },
};

interface Addon {
  id: string;
  name: string;
  price: number;
  impactQuantities: ImpactValues;
}

const createCawAddon = (tier: CawTier, currency: Currency, price: number): Addon => ({
  // Legacy addons use lowercase currency codes
  id: `climate-action-workforce-${tier}-${currency.toLowerCase()}`,
  price,
  impactQuantities: impactQuantities[tier],
  name: `${capitaliseFirstLetter(tier)} employees`,
});

export const cawAddonsGBP: Addon[] = [
  createCawAddon(CawTier.SEED, 'GBP', CAW_TIER_PRICES.seed.GBP),
  createCawAddon(CawTier.GROW, 'GBP', CAW_TIER_PRICES.grow.GBP),
  createCawAddon(CawTier.BLOOM, 'GBP', CAW_TIER_PRICES.bloom.GBP),
];

export const cawAddonsEUR: Addon[] = [
  createCawAddon(CawTier.SEED, 'EUR', CAW_TIER_PRICES.seed.EUR),
  createCawAddon(CawTier.GROW, 'EUR', CAW_TIER_PRICES.grow.EUR),
  createCawAddon(CawTier.BLOOM, 'EUR', CAW_TIER_PRICES.bloom.EUR),
];

export const cawAddonsUSD: Addon[] = [
  createCawAddon(CawTier.SEED, 'USD', CAW_TIER_PRICES.seed.USD),
  createCawAddon(CawTier.GROW, 'USD', CAW_TIER_PRICES.grow.USD),
  createCawAddon(CawTier.BLOOM, 'USD', CAW_TIER_PRICES.bloom.USD),
];

export const cawAddonsAUD: Addon[] = [
  createCawAddon(CawTier.SEED, 'AUD', CAW_TIER_PRICES.seed.AUD),
  createCawAddon(CawTier.GROW, 'AUD', CAW_TIER_PRICES.grow.AUD),
  createCawAddon(CawTier.BLOOM, 'AUD', CAW_TIER_PRICES.bloom.AUD),
];

export const cawAddonsCAD: Addon[] = [
  createCawAddon(CawTier.SEED, 'CAD', CAW_TIER_PRICES.seed.CAD),
  createCawAddon(CawTier.GROW, 'CAD', CAW_TIER_PRICES.grow.CAD),
  createCawAddon(CawTier.BLOOM, 'CAD', CAW_TIER_PRICES.bloom.CAD),
];

export const cawAllAddons = [...cawAddonsGBP, ...cawAddonsEUR, ...cawAddonsUSD, ...cawAddonsAUD, ...cawAddonsCAD];
export const isCawAddon = (id: string) => cawAllAddons.some(a => a.id === id);
export const isSeedTier = (id: string) => id.startsWith('climate-action-workforce-seed');
export const isGrowTier = (id: string) => id.startsWith('climate-action-workforce-grow');
export const isBloomTier = (id: string) => id.startsWith('climate-action-workforce-bloom');
export const getCawTierFromAddonId = (id: string): CawTier => {
  if (isSeedTier(id)) return CawTier.SEED;
  if (isGrowTier(id)) return CawTier.GROW;
  if (isBloomTier(id)) return CawTier.BLOOM;
  throw new Error(`Invalid addon id: ${id}`);
};
